import { Auth } from "aws-amplify";

const SESSION_RETRIES = 3;
const SESSION_RETRY_MS = 50;

export type PostAuthState = {
    authState: "signedIn" | "verifyContact";
    authData: unknown;
};

type VerifiedContactResult = {
    verified?: Record<string, unknown>;
    unverified?: Record<string, unknown>;
};

let inFlight: Promise<PostAuthState> | null = null;

const wait = (ms: number) =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

const hasVerifiedContact = (data: VerifiedContactResult | undefined) =>
    Boolean(data?.verified && Object.keys(data.verified).length > 0);

const fetchSessionUser = async (): Promise<unknown | null> => {
    for (let attempt = 0; attempt < SESSION_RETRIES; attempt += 1) {
        try {
            return await Auth.currentAuthenticatedUser({ bypassCache: true });
        } catch {
            if (attempt < SESSION_RETRIES - 1) {
                await wait(SESSION_RETRY_MS * (attempt + 1));
            }
        }
    }
    return null;
};

const resolvePostAuthStateOnce = async (
    user: unknown,
): Promise<PostAuthState> => {
    const sessionUser = await fetchSessionUser();
    const authoritative = sessionUser ?? user;

    try {
        const data = (await Auth.verifiedContact(
            authoritative,
        )) as VerifiedContactResult;

        if (hasVerifiedContact(data)) {
            return { authState: "signedIn", authData: authoritative };
        }

        if (sessionUser) {
            return {
                authState: "verifyContact",
                authData: Object.assign(sessionUser, data),
            };
        }
    } catch {
        // Fall through to signedIn — do not trap the user on an auth screen.
    }

    return { authState: "signedIn", authData: user };
};

/**
 * Cognito attributes decide signedIn vs verifyContact. Concurrent callers
 * (SignIn / RequireNewPassword / Hub) share one in-flight decision so they
 * cannot disagree and flash Verify contact.
 */
export const resolvePostAuthState = (user: unknown): Promise<PostAuthState> => {
    if (!inFlight) {
        inFlight = resolvePostAuthStateOnce(user).finally(() => {
            inFlight = null;
        });
    }
    return inFlight;
};

export const applyPostAuthState = (
    user: unknown,
    changeState: (state: string, data?: unknown) => void,
    options?: {
        /** Keep a submit button busy until signedIn / verifyContact. */
        setLoading?: (loading: boolean) => void;
    },
): void => {
    const { setLoading } = options ?? {};
    let settled = false;

    resolvePostAuthState(user)
        .then(({ authState, authData }) => {
            settled = true;
            setLoading?.(false);
            changeState(authState, authData);
        })
        .catch(() => {
            settled = true;
            setLoading?.(false);
            changeState("signedIn", user);
        });

    // Amplify SignIn clears `loading` in `finally` right after checkContact
    // returns. Re-assert on the next microtask so the button stays busy for
    // the post-auth Cognito check.
    Promise.resolve().then(() => {
        if (!settled) {
            setLoading?.(true);
        }
    });
};
